import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import type { CannedReply } from '@repletoryreach/sdk-api/inbox';
import { Skeleton, Text } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { useT } from '../../../i18n/I18nProvider';
import { CANNED_REPLIES_KEY } from './CannedReplyPicker';

/**
 * Canned replies, one click away.
 *
 * They existed only behind the "/" trigger in the composer, which an agent has
 * to already know about. The settings sidebar lists them so they are
 * discoverable; the shortcut still works and is shown on each row, which is
 * how someone learns it. The numbers are positions in the list, nothing more.
 */
export function QuickReplies({ onInsert, disabled }: { onInsert: (reply: CannedReply) => void; disabled?: boolean }) {
  const t = useT();
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 200);

  const replies = useQuery({
    queryKey: [...CANNED_REPLIES_KEY, 'panel'],
    queryFn: () => inbox.listCannedReplies({ limit: 200 }),
    staleTime: 60_000
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = replies.data?.data ?? [];
    if (!term) return all.slice(0, 12);
    return all
      .filter((r) => r.shortcut.toLowerCase().includes(term) || r.title.toLowerCase().includes(term) || r.body.toLowerCase().includes(term))
      .slice(0, 12);
  }, [replies.data, search]);

  return (
    <div className="rr-inbox2__quick">
      <div className="rr-inbox2__search">
        <span className="rr-inbox2__search-icon" aria-hidden><SearchIcon /></span>
        <TextInput
          size="sm"
          type="search"
          className="rr-inbox2__search-input"
          placeholder={t('inbox.quickReplies.search')}
          aria-label={t('inbox.quickReplies.search')}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
      </div>

      {replies.isPending && <Skeleton height={72} />}

      {!replies.isPending && rows.length === 0 && (
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {search ? t('inbox.canned.noMatch') : t('inbox.quickReplies.empty')}
        </Text>
      )}

      {rows.length > 0 && (
        <ol className="rr-inbox2__replies">
          {rows.map((r, i) => (
            <li key={r.id}>
              <button
                type="button"
                className="rr-inbox2__reply"
                disabled={disabled}
                onClick={() => onInsert(r)}
                title={r.body}
              >
                <span className="rr-inbox2__reply-index" aria-hidden>{i + 1}</span>
                <span className="rr-inbox2__reply-body">{r.body}</span>
                <span className="rr-inbox2__reply-shortcut" aria-hidden>/{r.shortcut}</span>
              </button>
            </li>
          ))}
        </ol>
      )}

      <Link to="/settings/canned-replies" className="rr-inbox2__side-link">
        {t('inbox.canned.manage')}
        <ExternalIcon />
      </Link>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <circle cx="7" cy="7" r="4.6" />
      <path d="M10.5 10.5 14 14" />
    </svg>
  );
}
function ExternalIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6.5 3H3v10h10V9.5" />
      <path d="M9.5 2.5H13.5V6.5M13.5 2.5 8 8" />
    </svg>
  );
}
